<template>
    <ModalBase :show-modal="showModal" @close-modal="close" auto-h class="gk-modal-confirm">
        <template v-slot:titlebar>
            <h3>
                {{ $gettext('Update Cost Types') }}
            </h3>
        </template>

        <template v-slot:maincontent>
            <div class="accountant-customer-selector">
                <p class="text-muted small mr-3">
                    {{ $gettext('Select the customers whose cost types you want to update. The changes you make in the general ledger will affect these customers and will also apply to new customers who sign up for Gekko via an invite link.') }}
                </p>

                <div class="d-flex align-items-center justify-content-between flex-wrap">
                    <div class="form-check mb-0">
                        <input
                            id="select-all-accountant-customers"
                            class="form-check-input"
                            style="cursor: pointer;"
                            type="checkbox"
                            :checked="selectionMode === 'all' && !hasSelectionExceptions"
                            :indeterminate.prop="isSelectAllIndeterminate"
                            @change="toggleSelectAll($event.target.checked)">
                        <label class="form-check-label font-weight-bold" for="select-all-accountant-customers" style="cursor: pointer;">
                            {{ $gettext('Select all') }}
                        </label>
                    </div>
                </div>

                <div class="list-group list-group-flush mt-4 customer-list-scroll">
                    <div v-if="loading" class="list-group-item text-center py-4 customer-list-loading">
                        <div class="spinner-border text-primary customer-list-loading-spinner" role="status">
                            <span class="sr-only">{{ $gettext('Loading') }}</span>
                        </div>
                    </div>

                    <template v-else>
                        <label
                            v-for="(item, index) in items"
                            :key="item.id"
                            class="mb-0" style="cursor: pointer;">
                            <div class="d-flex align-items-center p-3 mr-3" :class="{ 'border-bottom': index !== items.length - 1 }">
                                <input
                                    :id="`accountant-customer-${item.id}`"
                                    style="cursor: pointer;"
                                    class=" mt-1 mr-3"
                                    type="checkbox"
                                    :checked="isSelected(item)"
                                    @change="toggleSelection(item)">

                                <div class="selector-option_content">
                                    <div class="selector-option_name">
                                        {{ getCustomerName(item) }}
                                    </div>
                                    <div class="selector-option_email text-muted small">
                                        {{ getCustomerEmail(item) }}
                                    </div>
                                </div>
                            </div>
                        </label>

                        <div v-if="!items.length" class="list-group-item text-center text-muted py-4">
                            {{ $gettext('No customers found.') }}
                        </div>
                    </template>
                </div>

                <div>
                    <div class="d-flex align-items-center justify-content-between mr-3">
                        <GekkoButton
                            @click="fetchConnections(previousPageUrl)"
                            :disabled="!previousPageUrl || loading"
                            variant="link"
                            class="text-info"
                            icon="fa-solid fa-arrow-left"
                            :text="$gettext('Previous')" />
                        <GekkoButton
                            @click="fetchConnections(nextPageUrl)"
                            :disabled="!nextPageUrl || loading"
                            variant="link"
                            class="text-info"
                            icon-right
                            icon="fa fa-arrow-right"
                            :text="$gettext('Next')" />
                    </div>
                </div>
            </div>
        </template>

        <template v-slot:actions>
            <GekkoButton
                @click="updateCostTypes()"
                :disabled="loading"
                :text="$gettext('Update cost types')" />
        </template>
    </ModalBase>
</template>

<script>
import { mapActions } from 'vuex'
import ModalBase from "@/components/base_components/ModalBase"
import GekkoButton from "@/components/GekkoButton"

export default {
    name: "AccountantsCustomerRadioSelector",
    components: {ModalBase, GekkoButton},
    props: {
        showModal: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            items: [],
            loading: false,
            nextPageUrl: null,
            previousPageUrl: null,
            selectionMode: 'some',
            selectedItemsMap: {},
            totalCount: 0,
            pageSize: 0,
            currentPage: 1,
        }
    },
    computed: {
        allCurrentPageSelected() {
            return this.items.length > 0 && this.items.every((item) => this.isSelected(item))
        },
        someCurrentPageSelected() {
            return this.items.some((item) => this.isSelected(item))
        },
        hasSelectionExceptions() {
            return this.selectionMode === 'exclude' && Object.keys(this.selectedItemsMap).length > 0
        },
        isSelectAllIndeterminate() {
            if (this.selectionMode === 'exclude') {
                return this.hasSelectionExceptions
            }

            if (this.selectionMode === 'all') {
                return false
            }

            return this.someCurrentPageSelected && !this.allCurrentPageSelected
        },
        totalPages() {
            if (!this.totalCount || !this.pageSize) {
                return 1
            }

            return Math.max(1, Math.ceil(this.totalCount / this.pageSize))
        }
    },
    watch: {
        showModal: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.initializeSelector()
                }
            }
        }
    },
    methods: {
        ...mapActions({
            getAccountantConnections: 'accountants/getAccountantConnections',
            getAccountantNextListConnections: 'accountants/getAccountantNextListConnections',
        }),
        initializeSelector() {
            this.resetSelection()
            this.fetchConnections()
        },
        resetSelection() {
            this.selectionMode = 'some'
            this.selectedItemsMap = {}
        },
        fetchConnections(pageUrl = null) {
            this.loading = true

            const request = pageUrl
                ? this.getAccountantNextListConnections(pageUrl)
                : this.getAccountantConnections({status: 'connected'})

            request.then((response) => {
                this.items = response.data.results || []
                this.nextPageUrl = response.data.next
                this.previousPageUrl = response.data.previous
                this.totalCount = response.data.count || this.items.length
                this.pageSize = this.items.length || this.pageSize
                this.currentPage = this.getPageFromUrl(pageUrl || response.request?.responseURL)
            }).catch((error) => {
                console.error('Failed to load accountant customers', error)
                this.items = []
                this.nextPageUrl = null
                this.previousPageUrl = null
                this.totalCount = 0
                this.pageSize = 0
                this.currentPage = 1
            }).finally(() => {
                this.loading = false
            })
        },
        getPageFromUrl(url) {
            if (!url) {
                return 1
            }

            try {
                const parsedUrl = new URL(url, window.location.origin)
                return Number(parsedUrl.searchParams.get('page') || 1)
            } catch (error) {
                return 1
            }
        },
        getCustomerName(item) {
            return item?.customer?.name || item?.customer?.created_by?.display_name || item?.name || '-'
        },
        getCustomerEmail(item) {
            return item?.customer?.email || item?.customer?.created_by?.email || '-'
        },
        isSelected(item) {
            if (this.selectionMode === 'all' || this.selectionMode === 'exclude') {
                return !this.selectedItemsMap[item.id]
            }

            return Boolean(this.selectedItemsMap[item.id])
        },
        toggleSelection(item) {
            if (this.selectionMode === 'all') {
                this.selectionMode = 'exclude'
                this.$set(this.selectedItemsMap, item.id, item)
                return
            }

            if (this.selectionMode === 'exclude') {
                if (this.selectedItemsMap[item.id]) {
                    this.$delete(this.selectedItemsMap, item.id)

                    if (Object.keys(this.selectedItemsMap).length === 0) {
                        this.selectionMode = 'all'
                    }
                } else {
                    this.$set(this.selectedItemsMap, item.id, item)
                }

                return
            }

            if (this.isSelected(item)) {
                this.$delete(this.selectedItemsMap, item.id)
                return
            }

            this.$set(this.selectedItemsMap, item.id, item)
        },
        toggleSelectAll(checked) {
            this.selectionMode = checked ? 'all' : 'some'
            this.selectedItemsMap = {}
        },
        updateCostTypes() {
            const customerIds = Object.values(this.selectedItemsMap).map((item) => item.customer?.id)
            this.$emit('accountant-customers-selected', {
                mode: this.selectionMode,
                customerIds: customerIds
            })
            this.close()
        },
        close() {
            this.resetSelection()
            this.$emit("close")
        }
    }

}
</script>

<style scoped>
.accountant-customer-selector {
    min-width: 32rem;
    max-width: 100%;
}

.customer-list-scroll {
    max-height: 26rem;
    overflow-y: auto;
}

.customer-list-loading {
    min-height: 416px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.customer-list-loading-spinner {
    width: 3rem;
    height: 3rem;
    border-width: 0.3em;
}

.selector-option_content {
    min-width: 0;
}

.selector-option_name {
    font-weight: 600;
    word-break: break-word;
}

.selector-option_email {
    word-break: break-word;
}
</style>